import { Button } from "@/components/ui/button";
import { buildMetadata } from "@/lib/metadata";
import { cn } from "@/lib/utils";
import { ProductStockUploadService } from "@/services/product-stock-upload-service";
import { useMutation } from "@tanstack/react-query";
import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import { ArrowLeft, CloudUpload } from "lucide-react";
import { useId, useRef, useState } from "react";
import { toast } from "sonner";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

type UploadListItem = {
  id: string;
  file: File;
};

const createUploadId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `upload-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const Route = createFileRoute("/_main/products/upload/")({
  component: RouteComponent,
  head: () => ({
    meta: buildMetadata({
      title: "Upload stock document • Products",
      description:
        "Import a purchase order or goods receipt to update stock levels.",
      keywords: ["products", "upload", "stock", "purchase order"],
    }),
  }),
});

function RouteComponent() {
  const inputId = useId();
  const dropRef = useRef<HTMLLabelElement>(null);
  const router = useRouter();
  const [uploadedFiles, setUploadedFiles] = useState<UploadListItem[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);

  const accept =
    ".csv,.xlsx,.xls,.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";

  const uploadMutation = useMutation({
    mutationFn: (files: File[]) => ProductStockUploadService.upload(files),
    onSuccess: (data) => {
      toast.success("File uploaded", {
        description: "Review matches before syncing stock.",
      });
      setUploadedFiles([]);
      router.navigate({
        to: "/products/upload/review/$sessionId",
        params: { sessionId: data.session_id },
      });
    },
    onError: () => {
      toast.error("Upload failed", {
        description:
          "Check the file type (CSV, Excel, PDF, or image) and try again.",
      });
    },
  });

  const pickFiles = (list: FileList | null) => {
    if (!list || list.length === 0) {
      return;
    }

    setUploadedFiles((prev) => {
      const existingSignatures = new Set(
        prev.map(
          ({ file }) => `${file.name}-${file.size}-${file.lastModified}`,
        ),
      );

      const nextItems = Array.from(list)
        .filter((next) => {
          if (next.size > MAX_FILE_SIZE) {
            toast.error(`"${next.name}" exceeds the 5MB limit.`);
            return false;
          }

          const lower = next.name.toLowerCase();
          if (
            !lower.endsWith(".csv") &&
            !lower.endsWith(".pdf") &&
            !lower.endsWith(".png") &&
            !lower.endsWith(".jpg") &&
            !lower.endsWith(".jpeg") &&
            !lower.endsWith(".xlsx") &&
            !lower.endsWith(".xls")
          ) {
            toast.error(`"${next.name}" is not a supported file type.`);
            return false;
          }

          const signature = `${next.name}-${next.size}-${next.lastModified}`;
          if (existingSignatures.has(signature)) {
            return false;
          }

          existingSignatures.add(signature);
          return true;
        })
        .map((file) => ({
          id: createUploadId(),
          file,
        }));

      if (nextItems.length === 0) {
        return prev;
      }

      return [...prev, ...nextItems];
    });
  };

  const hasFiles = uploadedFiles.length > 0;

  const handleContinue = () => {
    if (!hasFiles || uploadMutation.isPending) {
      return;
    }
    uploadMutation.mutate(uploadedFiles.map((item) => item.file));
  };

  return (
    <div className="space-y-8 p-4 md:p-8">
      <Link
        to="/products"
        className="inline-flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back to Products
      </Link>

      <div>
        <h1 className="font-semibold text-2xl tracking-tight">Products</h1>
        <p className="mt-1 text-muted-foreground text-sm">
          Import a purchase order or goods receipt to update stock levels.
        </p>
      </div>

      <section className="mx-auto w-full max-w-3xl">
        <label
          ref={dropRef}
          htmlFor={inputId}
          onDragEnter={(e) => {
            e.preventDefault();
            setIsDragActive(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragActive(true);
          }}
          onDragLeave={(e) => {
            if (
              dropRef.current &&
              !dropRef.current.contains(e.relatedTarget as Node)
            ) {
              setIsDragActive(false);
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragActive(false);
            pickFiles(e.dataTransfer.files);
          }}
          className={cn(
            "flex min-h-[320px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed bg-muted/30 px-6 py-12 text-center transition-colors",
            isDragActive
              ? "border-brand-darkGreen bg-brand-yellowGreen/15"
              : "border-muted-foreground/35",
          )}
        >
          <div className="flex size-20 items-center justify-center rounded-full bg-background shadow-sm">
            <CloudUpload className="size-9 text-muted-foreground" />
          </div>
          <p className="mt-6 font-semibold">
            <span className="text-foreground">Click to upload</span>
            <span className="text-muted-foreground"> or drag and drop</span>
          </p>
          <p className="mt-2 text-muted-foreground text-sm">
            CSV, Excel (.xlsx, .xls), PDF, PNG, JPG, JPEG (max. 5MB)
          </p>
          <input
            id={inputId}
            type="file"
            multiple
            className="sr-only"
            accept={accept}
            onChange={(e) => {
              pickFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </label>

        {uploadedFiles.length > 0 ? (
          <div className="mt-6 rounded-xl border bg-card">
            <div className="border-b px-4 py-3">
              <p className="font-medium">
                {uploadedFiles.length} file
                {uploadedFiles.length === 1 ? "" : "s"} selected
              </p>
            </div>
            <div className="divide-y">
              {uploadedFiles.map(({ id, file }) => (
                <div
                  key={id}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="text-left">
                    <p className="font-medium">{file.name}</p>
                    <p className="text-muted-foreground text-sm">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setUploadedFiles((prev) =>
                        prev.filter((entry) => entry.id !== id),
                      )
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex justify-end border-t px-4 py-3">
              <Button
                type="button"
                size="sm"
                className="rounded-lg bg-foreground text-background hover:bg-foreground/90"
                disabled={uploadMutation.isPending}
                onClick={handleContinue}
              >
                {uploadMutation.isPending ? "Uploading…" : "Continue to review"}
              </Button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
